import * as React from 'react';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { CartEntryType } from 'utils/cart/types';

import { EntryDescriptionRow } from './cart-entry.styled';

function modifierRowText(
  child: IBackendCartEntries,
  currencyFormatter: Function,
  showAllPrices: boolean
): string | null {
  const name = child.name && child.name !== 'Combo Sides' ? child.name : '';
  const price =
    (child.type === CartEntryType.itemOptionModifier || showAllPrices) && child.price
      ? ` - ${currencyFormatter(child.price)}`
      : '';
  const line = `${name}${price}`.trim();
  return line.length > 0 ? line : null;
}

/** Plain-text lines for modifiers/options (same visibility as `resolveEntryChild`). */
export function collectEntryChildDescriptionParts(
  entry: IBackendCartEntries,
  currencyFormatter: Function,
  showAllPrices: boolean
): string[] {
  const out: string[] = [];
  if (!entry.children?.length) {
    return out;
  }

  const walk = (child: IBackendCartEntries, parent?: IBackendCartEntries) => {
    const hasChildren = Boolean(child.children?.length);
    const inComboSlot = parent?.type === CartEntryType.comboSlot;
    const isSlotLineItem = inComboSlot && child.type === CartEntryType.item;

    if (hasChildren) {
      if (isSlotLineItem && child.name !== 'Combo Sides') {
        const line = modifierRowText(child, currencyFormatter, showAllPrices);
        if (line) {
          out.push(line);
        }
      }
      child.children!.forEach(g => walk(g, child));
      return;
    }

    const line = modifierRowText(child, currencyFormatter, showAllPrices);
    if (line) {
      out.push(line);
    }
  };

  entry.children.forEach(c => walk(c, entry));
  return out;
}

const rowA11yProps = { importantForAccessibility: 'no-hide-descendants' as const };

const resolveEntryChild = (
  entry: IBackendCartEntries,
  currencyFormatter: Function,
  showAllPrices: boolean
) => {
  if (!entry.children) {
    return null;
  }

  const resolveChild = (
    child: IBackendCartEntries,
    i: number,
    parent?: IBackendCartEntries
  ): React.ReactNode => {
    const hasChildren = Array.isArray(child.children) && child.children.length > 0;

    const key = `${child.lineId ?? child.sanityId ?? child.name ?? 'row'}-${i}`;
    const inComboSlot = parent?.type === CartEntryType.comboSlot;
    const isSlotLineItem = inComboSlot && child.type === CartEntryType.item;

    const row = (rowKey: string) => (
      <EntryDescriptionRow key={rowKey} {...rowA11yProps}>
        {child.name !== 'Combo Sides' && child.name}
        {(child.type === CartEntryType.itemOptionModifier || showAllPrices) && child.price
          ? ` - ${currencyFormatter(child.price)}`
          : null}
      </EntryDescriptionRow>
    );

    // Combo slot parents (sides, drinks, etc.): render the line item even when it has nested children
    // (e.g. salad + dressing), then recurse into modifiers.
    if (hasChildren) {
      return (
        <React.Fragment key={key}>
          {isSlotLineItem && child.name !== 'Combo Sides' ? row(`${key}-self`) : null}
          {child.children!.map((g, idx) => resolveChild(g, idx, child))}
        </React.Fragment>
      );
    }

    const leafKey = isSlotLineItem && child.name !== 'Combo Sides' ? `${key}-self` : key;
    return row(leafKey);
  };

  return entry.children.map((child, i) => resolveChild(child, i, entry));
};

export default resolveEntryChild;
